import { useMemo } from 'react';

import { InfoBox } from 'components/InfoBox/InfoBox';
import { MultiImagePicker } from 'components/MultiImagePicker/MultiImagePicker';
import { useI18n } from 'i18n/hooks/useI18n';

import { Field } from './Field/Field';
import { useFormContext } from './FormContext';

type Props = {
  width?: number;
  height?: number;
  altTextRequired?: boolean;
  allowSVG?: boolean;
  allowVideo?: boolean;
  description?: string;
  extraAnalyticsData?: Record<string, unknown>;
  label?: string;
  name: string;
};

type MultiImagePickerFieldFilteredError = {
  index: number;
  message: string;
};

export function MultiImagePickerField({
  width,
  height,
  altTextRequired,
  allowVideo,
  allowSVG,
  description,
  extraAnalyticsData,
  label,
  name,
  ...props
}: Props) {
  const { i18n } = useI18n();
  const { errors } = useFormContext();

  const filteredErrors = useMemo(
    () =>
      Object.keys(errors).reduce((acc, key) => {
        if (!key.startsWith(name)) return acc;

        const keyParts = key.split('.');
        const index = parseInt(keyParts[1], 10);

        return [
          ...acc,
          { index, message: errors[key]! },
        ] satisfies MultiImagePickerFieldFilteredError[];
      }, [] as MultiImagePickerFieldFilteredError[]),
    [errors, name],
  );

  return (
    <Field
      inputProps={props}
      name={name}
      label={label}
      description={description}
      renderInput={({ value, qaId, updateValue }) => (
        <>
          {filteredErrors.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {filteredErrors.map((error) => (
                <InfoBox
                  key={error.index}
                  className="error"
                  $type="danger"
                  $marginBottom={4}
                >
                  {i18n(`Video or image %(index)s: %(message)s`, {
                    index: error.index + 1,
                    message: error.message,
                  })}
                </InfoBox>
              ))}
            </div>
          )}

          <MultiImagePicker
            width={width}
            height={height}
            altTextRequired={altTextRequired}
            extraAnalyticsData={extraAnalyticsData}
            values={value}
            allowVideo={allowVideo}
            allowSVG={allowSVG}
            qaId={qaId}
            onChange={updateValue}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        </>
      )}
    />
  );
}
